import { BILLETS, ELEMENTS, elementHeading, personToken } from '../shared/domain';
import {
  createPerson,
  fetchPeople,
  patchPerson,
  photoUrl,
  uploadRosterPhoto,
  type Person,
} from './api';
import { compressImage } from './image-compress';
import { el, go, selectBox, textInput, toast } from './ui';

const PRESELECT = 'mdrc-preselect';

export function renderRoster(root: HTMLElement): () => void {
  let people: Person[] = [];
  let element = 'all';
  let showInactive = false;
  let alive = true;
  let adding = false;

  const filterRow = el('div', { className: 'chips' });
  const list = el('div', { className: 'stack' });
  const addBtn = el('button', { type: 'button', className: 'btn', textContent: 'Add person' });
  const formHost = el('div');
  addBtn.addEventListener('click', () => {
    adding = !adding;
    paintForm();
  });

  root.append(el('h1', { textContent: 'Roster' }), addBtn, formHost, filterRow, list);

  function paintFilters(): void {
    filterRow.replaceChildren();
    const options = [{ id: 'all', label: 'All' }, ...ELEMENTS.map((e) => ({ id: e.id, label: e.label }))];
    if (people.some((p) => !p.active)) {
      options.push({ id: 'inactive', label: showInactive ? 'Hide inactive' : 'Inactive' });
    }
    for (const option of options) {
      const on =
        option.id === 'inactive' ? showInactive : option.id === element && option.id !== 'inactive';
      const btn = el('button', {
        type: 'button',
        className: on ? 'chip on' : 'chip',
        textContent: option.label,
        'aria-pressed': on ? 'true' : 'false',
      });
      btn.addEventListener('click', () => {
        if (option.id === 'inactive') showInactive = !showInactive;
        else element = option.id;
        paintFilters();
        paintList();
      });
      filterRow.append(btn);
    }
  }

  function paintForm(): void {
    formHost.replaceChildren();
    if (!adding) return;
    const callSign = textInput({ placeholder: 'Call sign' });
    const billet = selectBox(
      BILLETS.map((b) => ({ value: b, label: b })),
      'Billet'
    );
    let picked = '';
    const elements = el('div', { className: 'chips' });
    const paintElements = () => {
      elements.replaceChildren();
      for (const item of ELEMENTS) {
        const on = picked === item.id;
        const btn = el('button', {
          type: 'button',
          className: on ? 'chip on' : 'chip',
          textContent: item.label,
          'aria-pressed': on ? 'true' : 'false',
        });
        btn.addEventListener('click', () => {
          picked = item.id;
          paintElements();
        });
        elements.append(btn);
      }
    };
    paintElements();
    const file = el('input', {
      type: 'file',
      accept: 'image/*',
      capture: 'environment',
      className: 'photo-input',
    }) as HTMLInputElement;
    const photoBtn = el('button', { type: 'button', className: 'btn', textContent: 'Add photo' });
    photoBtn.addEventListener('click', () => file.click());
    const edipi = textInput({ placeholder: 'EDIPI' });
    const age = textInput({ placeholder: 'Age' });
    const blood = textInput({ placeholder: 'Blood type' });
    const allergies = textInput({ placeholder: 'Allergies' });
    const save = el('button', { type: 'button', className: 'btn btn-primary', textContent: 'Save' });
    const error = el('p', { className: 'form-error' });
    save.addEventListener('click', () => {
      void (async () => {
        error.textContent = '';
        if (!callSign.value.trim()) {
          error.textContent = 'Add a call sign';
          return;
        }
        if (!billet.value) {
          error.textContent = 'Pick a billet';
          return;
        }
        if (!picked) {
          error.textContent = 'Pick an element';
          return;
        }
        save.disabled = true;
        try {
          const person = await createPerson({
            call_sign: callSign.value.trim(),
            billet: billet.value,
            element: picked,
            edipi: edipi.value.trim(),
            age: age.value.trim(),
            blood_type: blood.value.trim(),
            allergies: allergies.value.trim(),
          });
          const chosen = file.files?.[0];
          if (chosen) {
            const compressed = await compressImage(chosen);
            await uploadRosterPhoto(
              person.id,
              new Blob([compressed.data], { type: compressed.contentType }),
              compressed.contentType
            );
          }
          adding = false;
          toast('Added to the roster', 'ok');
          await load();
        } catch (err) {
          error.textContent = (err as Error).message;
          save.disabled = false;
        }
      })();
    });
    formHost.append(
      el('div', { className: 'card' }, [
        el('label', { className: 'field' }, [
          el('span', { className: 'field-label', textContent: 'Call sign' }),
          callSign,
        ]),
        el('label', { className: 'field' }, [
          el('span', { className: 'field-label', textContent: 'Billet' }),
          billet,
        ]),
        el('div', { className: 'block' }, [
          el('span', { className: 'field-label', textContent: 'Element' }),
          elements,
        ]),
        fieldBlock('EDIPI', edipi),
        fieldBlock('Age', age),
        fieldBlock('Blood type', blood),
        fieldBlock('Allergies', allergies),
        photoBtn,
        file,
        error,
        save,
      ])
    );
  }

  function paintList(): void {
    const visible = people.filter((p) => {
      if (!showInactive && !p.active) return false;
      if (element !== 'all' && p.element !== element) return false;
      return true;
    });
    if (!visible.length) {
      list.replaceChildren(
        el('p', {
          className: 'muted',
          textContent: people.length
            ? 'Nobody in this filter.'
            : 'No one on the roster yet. Add a call sign.',
        })
      );
      return;
    }
    const groups = element === 'all' ? ELEMENTS.map((e) => e.id) : [element];
    const blocks: HTMLElement[] = [];
    for (const group of groups) {
      const members = visible.filter((p) => p.element === group);
      if (!members.length) continue;
      const section = el('section', { className: 'stack' });
      if (element === 'all') {
        section.append(el('h2', { textContent: elementHeading(group) }));
      }
      for (const person of members) section.append(card(person));
      blocks.push(section);
    }
    list.replaceChildren(...blocks);
  }

  function card(person: Person): HTMLElement {
    const btn = el('button', {
      type: 'button',
      className: person.active ? 'person-card' : 'person-card inactive',
    });
    const img = el('img', {
      alt: '',
      className: 'avatar',
    }) as HTMLImageElement;
    img.hidden = true;
    const mark = el('span', {
      className: 'avatar placeholder',
      textContent: initials(person.call_sign),
    });
    if (person.photo_key) {
      void photoUrl(person.photo_key)
        .then((url) => {
          img.src = url;
          img.hidden = false;
          mark.hidden = true;
        })
        .catch(() => undefined);
    }
    btn.append(
      el('span', { className: 'avatar-wrap' }, [img, mark]),
      el('span', { className: 'person-text' }, [
        el('strong', { textContent: person.call_sign }),
        el('span', {
          className: 'muted',
          textContent: personLine(person),
        }),
      ])
    );
    btn.addEventListener('click', () => go(`roster/${person.id}`));
    return btn;
  }

  async function load(): Promise<void> {
    try {
      people = await fetchPeople(true);
      if (!alive) return;
      people.sort((a, b) => a.call_sign.localeCompare(b.call_sign));
      paintFilters();
      paintForm();
      paintList();
    } catch (err) {
      if (alive) list.replaceChildren(el('p', { className: 'form-error', textContent: (err as Error).message }));
    }
  }

  void load();
  return () => {
    alive = false;
  };
}

export function renderPerson(root: HTMLElement, id: string): () => void {
  const host = el('div', { className: 'stack' });
  root.append(host);
  host.append(el('p', { className: 'muted', textContent: 'Loading…' }));
  let alive = true;
  let armed = false;

  void (async () => {
    try {
      const people = await fetchPeople(true);
      const person = people.find((p) => p.id === id);
      if (!alive) return;
      if (!person) {
        host.replaceChildren(el('p', { className: 'form-error', textContent: 'Not on the roster' }));
        return;
      }
      paint(person);
    } catch (err) {
      if (alive) host.replaceChildren(el('p', { className: 'form-error', textContent: (err as Error).message }));
    }
  })();

  function paint(person: Person): void {
    const callSign = textInput({ value: person.call_sign });
    const billet = selectBox(
      BILLETS.map((b) => ({ value: b, label: b })),
      'Billet'
    );
    billet.value = person.billet;
    let element = person.element;
    const elements = el('div', { className: 'chips' });
    const paintElements = () => {
      elements.replaceChildren();
      for (const item of ELEMENTS) {
        const on = element === item.id;
        const btn = el('button', {
          type: 'button',
          className: on ? 'chip on' : 'chip',
          textContent: item.label,
          'aria-pressed': on ? 'true' : 'false',
        });
        btn.addEventListener('click', () => {
          element = item.id;
          paintElements();
        });
        elements.append(btn);
      }
    };
    paintElements();
    const img = el('img', { alt: '', className: 'avatar large' }) as HTMLImageElement;
    if (person.photo_key) {
      void photoUrl(person.photo_key).then((url) => {
        img.src = url;
      });
    } else {
      img.hidden = true;
    }
    const file = el('input', {
      type: 'file',
      accept: 'image/*',
      capture: 'environment',
      className: 'photo-input',
    }) as HTMLInputElement;
    const photoBtn = el('button', { type: 'button', className: 'btn', textContent: 'Change photo' });
    photoBtn.addEventListener('click', () => file.click());
    const edipi = textInput({ value: person.edipi || '' });
    const age = textInput({ value: person.age || '' });
    const blood = textInput({ value: person.blood_type || '' });
    const allergies = textInput({ value: person.allergies || '' });
    const error = el('p', { className: 'form-error' });
    const save = el('button', { type: 'button', className: 'btn btn-primary', textContent: 'Save' });
    save.addEventListener('click', () => {
      void (async () => {
        error.textContent = '';
        save.disabled = true;
        try {
          let updated = await patchPerson(person.id, {
            call_sign: callSign.value.trim(),
            billet: billet.value,
            element,
            edipi: edipi.value.trim(),
            age: age.value.trim(),
            blood_type: blood.value.trim(),
            allergies: allergies.value.trim(),
            active: person.active,
          });
          const chosen = file.files?.[0];
          if (chosen) {
            const compressed = await compressImage(chosen);
            updated = await uploadRosterPhoto(
              person.id,
              new Blob([compressed.data], { type: compressed.contentType }),
              compressed.contentType
            );
          }
          toast('Roster updated', 'ok');
          paint(updated);
        } catch (err) {
          error.textContent = (err as Error).message;
          save.disabled = false;
        }
      })();
    });
    const spot = el('button', {
      type: 'button',
      className: 'btn btn-primary',
      textContent: 'New spot report',
    });
    spot.addEventListener('click', () => {
      sessionStorage.setItem(PRESELECT, personToken(person.id));
      go('spot');
    });
    const toggle = el('button', {
      type: 'button',
      className: 'btn btn-ghost',
      textContent: armed
        ? person.active
          ? 'Tap again to deactivate'
          : 'Tap again to activate'
        : person.active
          ? 'Deactivate'
          : 'Activate',
    });
    toggle.addEventListener('click', () => {
      if (!armed) {
        armed = true;
        toggle.textContent = person.active ? 'Tap again to deactivate' : 'Tap again to activate';
        return;
      }
      void (async () => {
        try {
          const updated = await patchPerson(person.id, { active: !person.active });
          armed = false;
          toast(updated.active ? 'Activated' : 'Deactivated', 'ok');
          paint(updated);
        } catch (err) {
          error.textContent = (err as Error).message;
        }
      })();
    });
    host.replaceChildren(
      img,
      el('label', { className: 'field' }, [
        el('span', { className: 'field-label', textContent: 'Call sign' }),
        callSign,
      ]),
      el('label', { className: 'field' }, [
        el('span', { className: 'field-label', textContent: 'Billet' }),
        billet,
      ]),
      el('div', { className: 'block' }, [
        el('span', { className: 'field-label', textContent: 'Element' }),
        elements,
      ]),
      fieldBlock('EDIPI', edipi),
      fieldBlock('Age', age),
      fieldBlock('Blood type', blood),
      fieldBlock('Allergies', allergies),
      photoBtn,
      file,
      error,
      el('div', { className: 'action-bar' }, [save]),
      spot,
      toggle
    );
  }

  return () => {
    alive = false;
  };
}

function fieldBlock(label: string, control: HTMLElement): HTMLElement {
  return el('label', { className: 'field' }, [
    el('span', { className: 'field-label', textContent: label }),
    control,
  ]);
}

function initials(callSign: string): string {
  const parts = callSign.trim().split(/\s+/);
  const name = parts[parts.length - 1] || callSign;
  return name.slice(0, 2).toUpperCase();
}

function personLine(person: Person): string {
  const job = person.rank ? `${person.rank} · ${person.billet}` : person.billet;
  return person.active ? job : `${job} · inactive`;
}
